import { useEffect, useState } from "react";
import { Country } from "country-state-city";
import useRequest from "../../../../components/hook/use-req";
import { AdSet, BudgetState, CreativeState, InstagramAdState, LocationOption, TIMEZONES, createAdSet } from "./types";

// Instagram ads run as real Meta ads (billed to the org's own Meta ad
// account, no wallet debit) — see instagram_ads.postman_collection.json.
// Everything here calls the same core connect API (useRequest's default
// base URL) the rest of the broadcast flow already uses for /campaign,
// /senders/..., etc.

// ─── Wire types (from the documented responses) ────────────────────────────

export interface InstagramAccount {
  ig_user_id: string;
  ig_username: string;
  ads_enabled: boolean;
  ad_account_id?: string | null;
  ad_account_name?: string | null;
}

export interface InstagramSender {
  id: string;
  name: string;
  instagram_account: InstagramAccount | null;
}

export interface LocationItem {
  key: string;
  type: "country" | "region" | "city";
  name: string;
  country_name?: string;
}

export interface TargetingItem {
  id: string;
  name: string;
  audience_size_lower?: number;
  audience_size_upper?: number;
}

export interface LocaleItem {
  key: number;
  name: string;
}

export interface CustomAudienceItem {
  id: string;
  name: string;
}

export interface ReachEstimate {
  estimate_ready: boolean;
  daily_reach_lower: number;
  daily_reach_upper: number;
  daily_active_users: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// ─── Debounced search — shared by locations/interests/behaviors/locales ────
// `buildParams` returning null means "don't fetch" (e.g. query too short);
// a 350ms debounce keeps keystrokes from firing a request each time.
export function useDebouncedSearch<T>(
  makeRequest: (data?: any, params?: Record<string, any>) => Promise<[any, number]>,
  buildParams: (q: string) => Record<string, any> | null,
  mapItems: (json: any) => T[],
  extraDep: string
) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = buildParams(query);
    if (!params) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const [json, status] = await makeRequest(undefined, params);
        if (cancelled) return;
        setItems(status >= 200 && status < 300 ? mapItems(json) : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, extraDep]);

  return { query, setQuery, items, loading };
}

// ─── Senders ────────────────────────────────────────────────────────────────
export function useInstagramSenders() {
  const token = localStorage.getItem("authToken");
  const { makeRequest, loading } = useRequest("/senders/instagram", "GET", { Authorization: `Bearer ${token}` });
  const [senders, setSenders] = useState<InstagramSender[]>([]);

  useEffect(() => {
    (async () => {
      const [json, status] = await makeRequest();
      if (status >= 200 && status < 300) setSenders(Array.isArray(json) ? json : json?.data || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { senders, loading };
}

// Defaults a fresh ad set's audience location to the signed-in user's own
// org country (GET /auth/get_userdata → payload.country, e.g. "Nigeria")
// instead of hardcoding India — same source use-default-country-code.ts
// uses for phone dial codes, resolved to a location key/name via
// country-state-city instead of a dial code.
export function useDefaultAdLocation() {
  const token = localStorage.getItem("authToken");
  const { makeRequest } = useRequest("/auth/get_userdata", "GET", { Authorization: `Bearer ${token}` });
  const [location, setLocation] = useState<LocationOption | null>(null);

  useEffect(() => {
    (async () => {
      const [response] = await makeRequest();
      const country = response?.payload?.country;
      if (!country) return;
      const match = Country.getAllCountries().find((c) => c.name.toLowerCase() === String(country).toLowerCase());
      if (match) setLocation({ key: match.isoCode, type: "country", name: match.name });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return location;
}

// ─── Location / targeting / locale search, custom audiences ───────────────
export function useLocationSearch(senderId: string) {
  const token = localStorage.getItem("authToken");
  const { makeRequest } = useRequest("/instagram/ads/search/locations", "GET", { Authorization: `Bearer ${token}` });
  return useDebouncedSearch<LocationItem>(
    makeRequest,
    (q) => (senderId ? { q: q || undefined, sender_id: senderId } : q.length >= 2 ? { q } : null),
    (json) => (Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []),
    senderId
  );
}

export function useTargetingSearch(type: "interests" | "behaviors", senderId: string) {
  const token = localStorage.getItem("authToken");
  const { makeRequest } = useRequest("/instagram/ads/search/targeting", "GET", { Authorization: `Bearer ${token}` });
  return useDebouncedSearch<TargetingItem>(
    makeRequest,
    (q) => (q.length >= 2 ? { q, type, sender_id: senderId || undefined } : null),
    (json) => (Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []),
    senderId
  );
}

export function useLocaleSearch(senderId: string) {
  const token = localStorage.getItem("authToken");
  const { makeRequest } = useRequest("/instagram/ads/search/locales", "GET", { Authorization: `Bearer ${token}` });
  return useDebouncedSearch<LocaleItem>(
    makeRequest,
    (q) => (q.length >= 2 ? { q, sender_id: senderId || undefined } : null),
    (json) => (Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []),
    senderId
  );
}

export function useCustomAudiences(senderId: string) {
  const token = localStorage.getItem("authToken");
  const { makeRequest } = useRequest("/instagram/ads/custom-audiences", "GET", { Authorization: `Bearer ${token}` });
  const [items, setItems] = useState<CustomAudienceItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!senderId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json] = await makeRequest(undefined, { sender_id: senderId });
      if (cancelled) return;
      setLoading(false);
      const data = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      setItems(data);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderId]);

  return { items, loading };
}

// ─── Ad creatives (real ones, not the old hardcoded AD_TEMPLATES) ──────────
// GET /instagram/ads/creatives — existing Meta ad creatives already created
// in the org's ad account, offered as real starting points for a new ad's
// media/copy on the Messaging step (per direct request — that list must not
// be a hardcoded sample set when this real endpoint already exists).
export interface AdCreativeItem {
  id: string;
  name?: string;
  thumbnail_url?: string;
  image_url?: string;
  call_to_action_type?: string;
  object_story_spec?: any;
}

export function useAdCreatives(senderId: string) {
  const token = localStorage.getItem("authToken");
  const { makeRequest, loading } = useRequest("/instagram/ads/creatives", "GET", { Authorization: `Bearer ${token}` });
  const [items, setItems] = useState<AdCreativeItem[]>([]);

  useEffect(() => {
    if (!senderId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest(undefined, { sender_id: senderId, limit: 50 });
      if (cancelled) return;
      if (status >= 200 && status < 300) {
        const data = Array.isArray(json?.items) ? json.items : Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
        setItems(data);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderId]);

  return { items, loading };
}

// Meta's own creative shape nests the actual copy under object_story_spec
// (link_data for an image/link ad, video_data for a video ad) — not
// documented field-by-field here, so this reads the common, stable keys and
// leaves a field blank rather than guessing at an undocumented one.
export function extractCreativeContent(c: AdCreativeItem): { primaryText?: string; headline?: string; cta?: string; mediaUrl?: string } {
  const spec = c.object_story_spec?.link_data || c.object_story_spec?.video_data;
  return {
    primaryText: spec?.message,
    headline: spec?.name,
    cta: spec?.call_to_action?.type || c.call_to_action_type,
    mediaUrl: c.thumbnail_url || c.image_url || spec?.picture,
  };
}

// ─── Payload mapping ────────────────────────────────────────────────────────
// UI label → Meta/API token. Kept here, next to the payload builder, so the
// two stay in sync — the UI never shows these raw tokens.
export const GOAL_TOKEN: Record<string, string> = {
  Sales: "sales",
  Traffic: "traffic",
  Leads: "leads",
  Awareness: "awareness",
  Engagement: "engagement",
};

export const OPTIMIZATION_GOAL_TOKEN: Record<string, string> = {
  Conversions: "OFFSITE_CONVERSIONS",
  "Link clicks": "LINK_CLICKS",
  "Landing page views": "LANDING_PAGE_VIEWS",
  Reach: "REACH",
};

export const SPECIAL_CATEGORY_TOKEN: Record<string, string> = {
  Credit: "CREDIT",
  Employment: "EMPLOYMENT",
  Housing: "HOUSING",
  "Social issues, elections or politics": "ISSUES_ELECTIONS_POLITICS",
};

export const PLACEMENT_TOKEN: Record<string, string> = {
  "Insta Feed": "feed",
  Stories: "story",
  Reels: "reels",
  "Explore Feed": "explore",
};

// A handful of well-known Meta ad CTA tokens — the docs list `call_to_action`
// as "a Meta CTA e.g. SHOP_NOW" without giving the full accepted set, so this
// sticks to ones that are stable across ad objectives rather than guessing
// objective-specific ones (e.g. INSTALL_APP, GET_QUOTE).
export const CTA_TOKEN_OPTIONS: { value: string; label: string }[] = [
  { value: "SHOP_NOW", label: "Shop Now" },
  { value: "LEARN_MORE", label: "Learn More" },
  { value: "SIGN_UP", label: "Sign Up" },
  { value: "BOOK_NOW", label: "Book Now" },
  { value: "CONTACT_US", label: "Contact Us" },
  { value: "SEND_MESSAGE", label: "Send Message" },
];

// A handful of well-known Meta standard pixel events — same caveat as
// CTA_TOKEN_OPTIONS above (the docs don't enumerate the full accepted set).
export const CUSTOM_EVENT_TYPES: { value: string; label: string }[] = [
  { value: "PURCHASE", label: "Purchase" },
  { value: "LEAD", label: "Lead" },
  { value: "COMPLETE_REGISTRATION", label: "Complete Registration" },
  { value: "ADD_TO_CART", label: "Add to Cart" },
  { value: "INITIATE_CHECKOUT", label: "Initiate Checkout" },
  { value: "ADD_PAYMENT_INFO", label: "Add Payment Info" },
  { value: "VIEW_CONTENT", label: "View Content" },
  { value: "SEARCH", label: "Search" },
  { value: "SUBSCRIBE", label: "Subscribe" },
  { value: "CONTACT", label: "Contact" },
];

// Confirmed live: "A 'sales' broadcast needs optimization.promoted_object on
// ad set 1 (e.g. a pixel_id and custom_event_type, or an app id)" — leads
// gets the same requirement per the doc's own field table.
const GOALS_NEEDING_PROMOTED_OBJECT = new Set(["Sales", "Leads"]);

const gendersFor = (g: AdSet["gender"]): number[] => (g === "men" ? [1] : g === "women" ? [2] : []);

const budgetSchedulePayload = (budget: BudgetState) => ({
  budget_type: budget.budgetType === "Daily Budget" ? "daily" : "lifetime",
  amount: budget.amount,
  start_time: budget.startAt ? new Date(budget.startAt).getTime() : null,
  end_time: budget.hasEnd && budget.endAt ? new Date(budget.endAt).getTime() : null,
});

// UTM has no dedicated field in the documented schema, so it's appended to
// the creative's own destination_url as a query string; "Destination Domain"
// has no backend slot at all and stays a UI-only note for now.
const withUtm = (url: string, utm: string): string => {
  if (!url || !utm.trim()) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${utm.trim().replace(/^[?&]/, "")}`;
};

const audiencePayload = (adSet: AdSet) => ({
  type: adSet.audienceType,
  locations: adSet.locations.map((l) => ({ key: l.key, type: l.type, name: l.name })),
  age_min: adSet.ageFrom,
  age_max: adSet.ageTo,
  genders: gendersFor(adSet.gender),
  languages: adSet.language ? [adSet.language.key] : [],
  custom_audience_ids: adSet.customAudiences.map((c) => c.id),
  interests: adSet.interests.map((i) => ({ id: i.id, name: i.name })),
  behaviors: adSet.behaviours.map((b) => ({ id: b.id, name: b.name })),
});

const placementsPayload = (adSet: AdSet) => ({
  type: adSet.placementMode === "optimised" ? "optimized" : "manual",
  positions: adSet.placementMode === "manual" ? adSet.manualPlacements.map((p) => PLACEMENT_TOKEN[p]).filter(Boolean) : [],
});

const optimizationPayload = (adSet: AdSet, broadcastGoal: string) => ({
  goal: OPTIMIZATION_GOAL_TOKEN[adSet.optimization] || "LINK_CLICKS",
  bid_strategy: "LOWEST_COST_WITHOUT_CAP",
  ...(GOALS_NEEDING_PROMOTED_OBJECT.has(broadcastGoal) && adSet.promotedObjectPixelId.trim()
    ? { promoted_object: { pixel_id: adSet.promotedObjectPixelId.trim(), custom_event_type: adSet.promotedObjectEventType || "PURCHASE" } }
    : {}),
});

const creativePayload = (creative: CreativeState, adSetName: string, utm: string) => ({
  name: `${adSetName} - Ad 1`,
  // Set whenever the creative was picked from an existing one
  // (GET /instagram/ads/creatives, see step-messaging.tsx) — templateId
  // holds that real Meta creative id — so the backend can reuse it instead
  // of only reconstructing a new creative from these fields.
  creative_id: creative.templateId || "",
  format: creative.format === "single" ? "single_image" : creative.format === "carousel" ? "carousel" : "reel_video",
  media_url: creative.media?.url || undefined,
  thumbnail_url: creative.format === "reel" ? creative.media?.url : undefined,
  primary_text: creative.primaryText || undefined,
  headline: creative.headline || undefined,
  call_to_action: creative.cta || undefined,
  destination_url: withUtm(creative.destinationUrl, utm),
});

// One creative/one budget is shared across every ad set — the wizard doesn't
// (yet) offer per-ad-set creative or budget editing, so the same values are
// applied to each entry in `ad_sets[]` rather than only the first.
export function buildContent(state: InstagramAdState, senderId: string) {
  return {
    broadcast_details: {
      sender_id: senderId || undefined,
      goal: GOAL_TOKEN[state.goal] || "traffic",
      name: state.name,
      buying_type: state.buyingType === "auction" ? "AUCTION" : "RESERVED",
      special_ad_categories: state.specialCategory ? [SPECIAL_CATEGORY_TOKEN[state.specialCategory]].filter(Boolean) : [],
    },
    ad_sets: state.adSets.map((adSet) => ({
      name: adSet.name || state.name,
      audience: audiencePayload(adSet),
      placements: placementsPayload(adSet),
      budget_schedule: budgetSchedulePayload(state.budget),
      optimization: optimizationPayload(adSet, state.goal),
      creatives: [creativePayload(state.creative, adSet.name || state.name, adSet.utm)],
    })),
  };
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const epochToLocalInput = (ms?: number | null): string => {
  if (!ms) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const defaultTodayAt = (hour: number) => {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return epochToLocalInput(d.getTime());
};

const reverseLookup = (map: Record<string, string>, value: string | undefined, fallback: string): string => {
  if (!value) return fallback;
  const hit = Object.entries(map).find(([, v]) => v.toLowerCase() === value.toLowerCase());
  return hit ? hit[0] : fallback;
};

// Reverses buildContent() — hydrates the wizard's local state from an
// already-saved campaign's `content` when resuming an existing Instagram
// draft (GET /campaign/details/{id}). Without this, opening a draft from the
// broadcast dashboard always started InstagramFlow from createInitialState()
// with campaignIdRef unset, so continuing to edit silently discarded
// everything picked in the earlier session and created a brand-new campaign
// (POST) on save/launch instead of updating the existing one (PATCH/PUT).
// Best-effort where the wire shape can't fully reconstruct a field (e.g. a
// custom audience's name, a locale's display name, or the raw UTM string
// already folded into destination_url) — those fall back to a placeholder
// rather than guessing.
export function parseInstagramContent(content: any): InstagramAdState {
  const bd = content?.broadcast_details || {};
  const wireAdSets: any[] = Array.isArray(content?.ad_sets) ? content.ad_sets : content?.ad_set ? [content.ad_set] : [];
  const firstAdSet = wireAdSets[0];
  const firstCreative = firstAdSet?.creatives?.[0];

  const adSets: AdSet[] = wireAdSets.length
    ? wireAdSets.map((a, i) => {
        const audience = a?.audience || {};
        const genders: number[] = Array.isArray(audience.genders) ? audience.genders : [];
        return {
          id: `adset_${Date.now()}_${i}`,
          name: a?.name || "",
          audienceType: audience.type === "manual" ? "manual" : "advantage",
          locations: Array.isArray(audience.locations) ? audience.locations : [],
          ageFrom: audience.age_min ?? 25,
          ageTo: audience.age_max ?? 45,
          gender: genders.includes(1) ? "men" : genders.includes(2) ? "women" : "all",
          language:
            Array.isArray(audience.languages) && audience.languages[0] != null
              ? { key: audience.languages[0], name: `Locale ${audience.languages[0]}` }
              : null,
          savedSegments: [],
          customAudiences: Array.isArray(audience.custom_audience_ids)
            ? audience.custom_audience_ids.map((id: string) => ({ id, name: id }))
            : [],
          interests: Array.isArray(audience.interests) ? audience.interests : [],
          behaviours: Array.isArray(audience.behaviors) ? audience.behaviors : [],
          placementMode: a?.placements?.type === "manual" ? "manual" : "optimised",
          manualPlacements: Array.isArray(a?.placements?.positions)
            ? a.placements.positions.map((p: string) => reverseLookup(PLACEMENT_TOKEN, p, "")).filter(Boolean)
            : ["Insta Feed", "Stories"],
          optimization: reverseLookup(OPTIMIZATION_GOAL_TOKEN, a?.optimization?.goal, "Conversions"),
          conversionLocation: "Website",
          attribution: "7-day click/ 1-day view",
          destinationDomain: "",
          utm: "",
          promotedObjectPixelId: a?.optimization?.promoted_object?.pixel_id || "",
          promotedObjectEventType: a?.optimization?.promoted_object?.custom_event_type || "PURCHASE",
        };
      })
    : [createAdSet(bd.name || "")];

  return {
    senderId: bd.sender_id || "",
    senderHandle: "",
    goal: reverseLookup(GOAL_TOKEN, bd.goal, "Sales"),
    name: bd.name || "",
    buyingType: bd.buying_type === "RESERVED" ? "reach" : "auction",
    specialCategory:
      Array.isArray(bd.special_ad_categories) && bd.special_ad_categories[0]
        ? reverseLookup(SPECIAL_CATEGORY_TOKEN, bd.special_ad_categories[0], "")
        : "",
    adSets,
    creative: {
      templateId: firstCreative?.creative_id || null,
      format: firstCreative?.format === "reel_video" ? "reel" : firstCreative?.format === "carousel" ? "carousel" : "single",
      media: firstCreative?.media_url
        ? { url: firstCreative.media_url, kind: firstCreative.format === "reel_video" ? "video" : "image", name: "" }
        : null,
      primaryText: firstCreative?.primary_text || "",
      headline: firstCreative?.headline || "",
      cta: firstCreative?.call_to_action || "",
      destinationUrl: firstCreative?.destination_url || "",
    },
    budget: {
      budgetType: firstAdSet?.budget_schedule?.budget_type === "lifetime" ? "Lifetime Budget" : "Daily Budget",
      amount: firstAdSet?.budget_schedule?.amount ?? 1000,
      startAt: epochToLocalInput(firstAdSet?.budget_schedule?.start_time) || defaultTodayAt(14),
      timezone: TIMEZONES[0],
      hasEnd: !!firstAdSet?.budget_schedule?.end_time,
      endAt: epochToLocalInput(firstAdSet?.budget_schedule?.end_time) || defaultTodayAt(16),
    },
  };
}

// Only what's needed for POST /instagram/ads/reach-estimate — the doc scopes
// it to one ad set at a time (the one currently being edited).
export function buildReachEstimateBody(adSet: AdSet, goal: string, senderId: string) {
  return {
    sender_id: senderId || undefined,
    goal: GOAL_TOKEN[goal] || "traffic",
    ad_set: {
      name: adSet.name || "Ad Set",
      audience: audiencePayload(adSet),
      placements: placementsPayload(adSet),
      budget_schedule: { budget_type: "daily", amount: 10, start_time: null, end_time: null },
      optimization: optimizationPayload(adSet, goal),
    },
  };
}
